import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { faHeart, faPlus } from '@fortawesome/pro-solid-svg-icons';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Link } from '@sorare/routing';

import playerPlaceholderURL from '@sorare/core/src/assets/players/placeholder.png';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { LoadingButton } from '@sorare/core/src/atoms/buttons/LoadingButton';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { SearchInput } from '@sorare/core/src/atoms/inputs/SearchInput';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LoadingIndicator } from '@sorare/core/src/atoms/loader/LoadingIndicator';
import { BodyS, HeadlineM, LabelS } from '@sorare/core/src/atoms/typography';
import { Ellipsis } from '@sorare/core/src/atoms/typography/Ellipsis';
import { ANY_SPORT_SCOUTING_CENTER_WATCHLISTS_ID } from '@sorare/core/src/constants/__generated__/routes';
import { useIntlContext } from '@sorare/core/src/contexts/intl';
import { useSnackNotificationContext } from '@sorare/core/src/contexts/snackNotification';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { idFromObject } from '@sorare/core/src/gql/idFromObject';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { useToggleSubscription } from '@sorare/core/src/hooks/subscriptions/useToggleSubscription';
import { useIsMobileApp } from '@sorare/core/src/hooks/useIsMobileApp';
import { glossary } from '@sorare/core/src/lib/glossary';
import { regularMaterial } from '@sorare/core/src/style/utils';

import { AddWatchlistButton } from '../AddWatchlistButton';
import { EmptyState } from './EmptyState';
import type {
  AddToWatchlistQuery,
  AddToWatchlistQueryVariables,
  AddToWatchlist_anyPlayer,
} from './__generated__/index.graphql';
import { useAddToWatchlist } from './useAddToWatchlist';
import { useCreateAndAddToWatchlist } from './useCreateAndAddToWatchlist';

const Root = styled(Vertical).attrs({ gap: 0 })`
  position: relative;
  isolation: isolate;
  width: 384px;
  height: 366px;
  overflow-y: auto;
`;

const FavoritesContainer = styled(Vertical)`
  width: 40px;
  height: 40px;
  border-radius: var(--half-unit);
  background-color: var(--c-nd-50);
  flex-shrink: 0;
`;

const WatchlistAvatar = styled.img`
  width: 40px;
  height: 40px;
  padding-top: var(--half-unit);
  object-fit: contain;
  border-radius: var(--half-unit);
  background-color: var(--c-nd-50);
  flex-shrink: 0;
`;

const Header = styled(Vertical)`
  padding: var(--double-unit);
  z-index: 1;
  position: sticky;
  top: 0;
  ${regularMaterial}
`;

const Content = styled(Vertical).attrs({ gap: 0.5 })`
  ${regularMaterial}
  padding: 0 var(--double-unit) var(--double-unit);
  flex: 1;
`;

const WatchlistName = styled(BodyS).attrs({ bold: true })`
  flex: 1;
`;

const AddButton = styled(Button).attrs({ size: 'small' })``;

const StyledButtonBase = styled(ButtonBase)`
  padding: var(--half-unit) var(--intermediate-unit) var(--half-unit)
    var(--half-unit);
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: var(--double-unit);
  border-radius: var(--unit);
  ${AddButton} {
    display: none;
  }
  &:hover {
    background-color: var(--c-nd-50);
    ${AddButton} {
      display: flex;
    }
  }
`;

const NoResults = styled(BodyS)`
  padding: var(--intermediate-unit);
  min-height: calc(var(--unit) * 6);
`;

const StyledLink = styled(Link)`
  text-decoration: underline;
`;

const Names = styled(Vertical)`
  flex: 1;
  width: 100%;
`;

const AlreadyInFavorites = styled(LabelS)`
  display: none;
  text-align: left;
  ${StyledButtonBase}:hover & {
    display: block;
  }
`;

const LoadingContainer = styled(Horizontal)`
  padding: var(--unit);
`;

type Props = {
  anyPlayer: AddToWatchlist_anyPlayer;
  onClose?: () => void;
};

const ADD_TO_WATCHLIST_QUERY = gql`
  query AddToWatchlistQuery($slug: String!, $search: String!, $sport: Sport!) {
    anyPlayer(slug: $slug) {
      slug
      currentUserSubscription {
        slug
      }
    }
    currentUser {
      slug
      myWatchlists(query: $search, sport: $sport) {
        slug
        id
        title
        playersPanel {
          id
          anyPlayer {
            slug
            squaredPictureUrl
          }
        }
      }
    }
  }
` as TypedDocumentNode<AddToWatchlistQuery, AddToWatchlistQueryVariables>;

export const AddToWatchlist = ({
  anyPlayer: anyPlayerProp,
  onClose,
}: Props) => {
  const { isIosApp } = useIsMobileApp();
  const { formatMessage } = useIntlContext();
  const [search, setSearch] = useState('');
  const { generateSportContextPath } = useSportContext();
  const { addToWatchlist } = useAddToWatchlist();
  const { createAndAddToWatchlist } = useCreateAndAddToWatchlist();
  const { showNotification } = useSnackNotificationContext();

  const { data, loading, refetch } = useQuery(ADD_TO_WATCHLIST_QUERY, {
    variables: { slug: anyPlayerProp.slug, search, sport: anyPlayerProp.sport },
    skip: !anyPlayerProp,
  });

  const anyPlayer = data?.anyPlayer;

  const toggleSubscription = useToggleSubscription(
    anyPlayer?.currentUserSubscription,
    anyPlayer || anyPlayerProp
  );

  const alreadyInFavorites = !!anyPlayer?.currentUserSubscription;

  const onSuccess = ({ title, id }: { title: string; id: string }) => {
    showNotification('playerAddedToWatchlist', {
      player: anyPlayerProp.displayName,
      watchlist: title,
      link: (chunks: string) =>
        id && !isIosApp ? (
          <StyledLink
            to={generateSportContextPath(
              ANY_SPORT_SCOUTING_CENTER_WATCHLISTS_ID,
              {
                params: {
                  id: idFromObject(id),
                },
              }
            )}
          >
            {chunks}
          </StyledLink>
        ) : (
          chunks
        ),
    });
    onClose?.();
  };

  const watchlists = data?.currentUser?.myWatchlists || [];
  const commonPlayerId = anyPlayerProp.commonPlayer?.id;
  if (!commonPlayerId) return null;

  const isEmpty = watchlists?.length === 0;

  return (
    <Root>
      <Header>
        <HeadlineM className="text-center">
          <FormattedMessage
            id="addToWatchlist.title"
            defaultMessage="Add {player} to"
            values={{ player: anyPlayerProp.displayName }}
          />
        </HeadlineM>
        <Horizontal>
          <SearchInput
            small
            withIcon
            value={search}
            onChange={({ currentTarget }) => setSearch(currentTarget.value)}
            placeholder={formatMessage(glossary.search)}
          />
          <AddWatchlistButton
            size="small"
            color="tertiary"
            onSuccess={() => {
              refetch();
            }}
          />
        </Horizontal>
      </Header>
      <Content>
        <StyledButtonBase
          key="favorites"
          onClick={() => {
            if (!alreadyInFavorites) {
              toggleSubscription();
            }
          }}
          disabled={addToWatchlist.loading || alreadyInFavorites}
        >
          <FavoritesContainer center>
            <FontAwesomeIcon icon={faHeart} size="lg" />
          </FavoritesContainer>
          <Names gap={0}>
            <WatchlistName bold className="text-left">
              <Ellipsis>
                <FormattedMessage {...glossary.favorites} />
              </Ellipsis>
            </WatchlistName>
            {!!alreadyInFavorites && (
              <AlreadyInFavorites color="var(--c-nd-600)">
                <FormattedMessage
                  id="addToWatchlist.alreadyInFavorites"
                  defaultMessage="Already in favorites"
                />
              </AlreadyInFavorites>
            )}
          </Names>
          <AddButton
            size="small"
            color="secondary"
            disabled={alreadyInFavorites}
          >
            <FormattedMessage id="addToWatchlist.add" defaultMessage="Add" />
          </AddButton>
        </StyledButtonBase>
        {loading ? (
          <LoadingContainer>
            <LoadingIndicator small />
          </LoadingContainer>
        ) : (
          <>
            {isEmpty ? (
              <>
                {search ? (
                  <NoResults className="text-center">
                    <FormattedMessage {...glossary.noResultsFound} />
                  </NoResults>
                ) : (
                  <EmptyState onAddWatchlist={refetch} />
                )}
              </>
            ) : (
              watchlists.map(watchlist => (
                <StyledButtonBase
                  key={watchlist.id}
                  onClick={() => {
                    addToWatchlist
                      .mutate(watchlist.id, [commonPlayerId])
                      .then(() => {
                        onSuccess(watchlist);
                      });
                  }}
                  disabled={addToWatchlist.loading}
                >
                  <WatchlistAvatar
                    alt=""
                    src={
                      watchlist?.playersPanel?.[0]?.anyPlayer
                        ?.squaredPictureUrl || playerPlaceholderURL
                    }
                  />
                  <WatchlistName bold className="text-left">
                    <Ellipsis>{watchlist.title}</Ellipsis>
                  </WatchlistName>
                  <AddButton size="small" color="secondary">
                    <FormattedMessage
                      id="addToWatchlist.add"
                      defaultMessage="Add"
                    />
                  </AddButton>
                </StyledButtonBase>
              ))
            )}
          </>
        )}

        {!!search && (
          <LoadingButton
            loading={createAndAddToWatchlist.loading}
            size="small"
            color="tertiary"
            onClick={async () =>
              createAndAddToWatchlist
                .mutate(search, [commonPlayerId])
                .then(d => {
                  const watchlist = d?.data?.upsertWatchlist?.watchlist;
                  if (watchlist) {
                    onSuccess(watchlist);
                  }
                })
            }
            startIcon={<FontAwesomeIcon icon={faPlus} />}
          >
            <FormattedMessage
              id="addToWatchlist.createAndAdd"
              defaultMessage="Create and add to “{search}”"
              values={{
                search:
                  search.length > 12 ? `${search.slice(0, 10)}...` : search,
              }}
            />
          </LoadingButton>
        )}
      </Content>
    </Root>
  );
};

AddToWatchlist.fragments = {
  anyPlayer: gql`
    fragment AddToWatchlist_anyPlayer on AnyPlayerInterface {
      slug
      sport
      displayName
      commonPlayer(rarity: common) {
        id
      }
    }
  ` as TypedDocumentNode<AddToWatchlist_anyPlayer>,
};
